import { observer } from "mobx-react-lite";
import { useState } from "react";
import { useSearchStore, useViewSettingsStore } from "../..";
import useBodyContainerClassName from "../../hooks/useBodyContainerClassName";
import useNavigateToCountyInsights from "../../hooks/useNavigateToCountyInsights";
import useNavigateToMunicipalityInsights from "../../hooks/useNavigateToMunicipalityInsights";
import usePropertyEstimates from "../../hooks/usePropertyEstimates";
import IProperty from "../../interfaces/IProperty";
import SearchType from "../../interfaces/SearchType";
import { formatDate, formatNumber, formatPrice } from "../../utils/utils";
import commonStyles from "../Common.module.css";
import detailsStyles from "../Details.module.css";
import FieldRow from "../FieldRow";
import Spinner from "../spinners/Spinner";
import PropertyContactInfoPanel from "./PropertyContactInfoPanel";
import { MonthlyRentalComparableCompsInfo, SalePricePerAcreComparableCompsInfo, SalePricePerSqFtComparableCompsInfo } from "./PropertyEstimatesCompsPanel";
import PropertyIndustrialScarcityRow from "./PropertyIndustrialScarcityRow";
import ZoningDetailsModal from "./ZoningDetailsModal";

function PropertySummary({ property }: IPropertySummaryProps) {
    const searchStore = useSearchStore();
    const viewSettingsStore = useViewSettingsStore();
    const bodyContainerClassName = useBodyContainerClassName();
    const propertyEstimates = usePropertyEstimates(property);
    const navigateToCountyInsights = useNavigateToCountyInsights();
    const navigateToMunicipalityInsights = useNavigateToMunicipalityInsights();
    const [showZoningDetails, setShowZoningDetails] = useState(false);

    return (
        <div className={bodyContainerClassName}>
            <div className={detailsStyles.columns}>
                <div className={[detailsStyles.column, commonStyles.rowSeparated].join(" ")}>
                    <div className={commonStyles.header}>Physical</div>
                    <FieldRow label="BUILDING SIZE">
                        {!!property.buildingSize && `${formatNumber(property.buildingSize)} Sq-Ft`}
                    </FieldRow>
                    <FieldRow label="LAND SIZE">
                        {!!property.landSize && `${formatNumber(property.landSize)} Acres`}
                    </FieldRow>
                    <FieldRow label="NUMBER OF BUILDINGS">
                        {!!property.numberOfBuildings && formatNumber(property.numberOfBuildings)}
                    </FieldRow>
                    <FieldRow label="NUMBER OF UNITS">
                        {!!property.numberOfUnits && formatNumber(property.numberOfUnits)}
                    </FieldRow>
                    <FieldRow label="YEAR BUILT">
                        {property.yearBuilt}
                    </FieldRow>
                    <FieldRow label="EXISTING FAR">
                        {!!property.currentFar && Math.round((property.currentFar + Number.EPSILON) * 100) / 100}
                    </FieldRow>
                    <FieldRow label="GRADE CHANGE">
                        {!!property.gradeChangeFeet && `${formatNumber(property.gradeChangeFeet)} Ft`}
                    </FieldRow>
                    <FieldRow label="DISTANCE TO POWER">
                        {!!property.distanceToPowerFeet && `${formatNumber(property.distanceToPowerFeet)} Ft`}
                    </FieldRow>
                </div>
                <div className={detailsStyles.columns}>
                    <div className={[detailsStyles.column, commonStyles.rowSeparated].join(" ")}>
                        <div className={commonStyles.header}>Estimates</div>
                        {!(propertyEstimates.monthlyRental || propertyEstimates.salePricePerAcre || propertyEstimates.salePricePerSqFt) &&
                            <div className={commonStyles.key}>Not enough data available</div>
                        }
                        <FieldRow label="MONTHLY RENTAL RATE">
                            {propertyEstimates.monthlyRental && (
                                <>
                                    {`${formatPrice(propertyEstimates.monthlyRental)}/Acre`}
                                    {propertyEstimates.isMonthlyRentalLimited && " * Limited Comps"}
                                </>
                            )}
                        </FieldRow>
                        <MonthlyRentalComparableCompsInfo data={propertyEstimates.monthlyRentalComparableComps} />
                        <FieldRow label="SALE PRICE PER USABLE ACRE">
                            {propertyEstimates.salePricePerAcre && (
                                <>
                                    {formatPrice(propertyEstimates.salePricePerAcre)}
                                    {propertyEstimates.isSalePricePerAcreLimited && " * Limited Comps"}
                                </>
                            )}
                        </FieldRow>
                        <SalePricePerAcreComparableCompsInfo data={propertyEstimates.salePricePerAcreComparableComps} />
                        <FieldRow label="SALE PRICE PER SQ-FT">
                            {propertyEstimates.salePricePerSqFt && (
                                <>
                                    {formatPrice(propertyEstimates.salePricePerSqFt)}
                                    {propertyEstimates.isSalePricePerSqFtLimited && " * Limited Comps"}
                                </>
                            )}
                        </FieldRow>
                        <SalePricePerSqFtComparableCompsInfo data={propertyEstimates.salePricePerSqFtComparableComps} />
                        <FieldRow label="LOCATION GRADE">
                            {propertyEstimates.isGradeLoading
                                ? <Spinner />
                                : propertyEstimates.locationGrade}
                        </FieldRow>
                        <PropertyIndustrialScarcityRow centerPoint={property.centerPoint} />
                    </div>
                </div>
                <div className={[detailsStyles.column, commonStyles.rowSeparated].join(" ")}>
                    <div className={commonStyles.header}>Summary</div>
                    <FieldRow label="PARCEL ID">{property.parcelId}</FieldRow>
                    <FieldRow label="COUNTY">
                        {!!property.county && (viewSettingsStore.isMobile
                            ? property.county
                            : <span className={commonStyles.link} onClick={() => navigateToCountyInsights(property.county, property.centerPoint)}>{property.county}</span>)
                        }
                    </FieldRow>
                    <FieldRow label="MUNICIPALITY">
                        {!!property.municipality && (viewSettingsStore.isMobile
                            ? property.municipality
                            : <span className={commonStyles.link} onClick={() => navigateToMunicipalityInsights(property.municipality, property.centerPoint)}>{property.municipality}</span>)
                        }
                    </FieldRow>
                    <FieldRow label="TYPE OF STRUCTURE">{property.typeOfStructure}</FieldRow>
                    <FieldRow label="SITE DESCRIPTION">{property.siteDescription}</FieldRow>
                    <FieldRow label="OWNERSHIP STRUCTURE">{property.ownershipStructure}</FieldRow>
                    <FieldRow label={<>ZONING <span className={commonStyles.link} onClick={() => setShowZoningDetails(true)}>(Details)</span></>}>
                        {!!property.zoning &&
                            <span>
                                {property.zoning} {property.zoningDescription && `(${property.zoningDescription})`}
                            </span>
                        }
                    </FieldRow>
                    {showZoningDetails && <ZoningDetailsModal property={property} onClose={() => setShowZoningDetails(false)} />}
                    <FieldRow label="FLOODPLAIN">
                        <a href={`https://msc.fema.gov/portal/search?AddressQuery=${property.centerPoint.longitude},${property.centerPoint.latitude}`} target="_blank">
                            {!!property.femaFloodZone ? `${property.femaFloodZone} (${property.femaFloodZoneSubtype})` : "---"}
                        </a>
                    </FieldRow>
                    <FieldRow label="FEMA RISK RATING">
                        {property.femaRiskRating}
                    </FieldRow>
                </div>
            </div>
            <div className={detailsStyles.columns}>
                <div className={[detailsStyles.column, commonStyles.rowSeparated].join(" ")}>
                    <div className={commonStyles.header}>Ownership</div>
                    <FieldRow label="OWNER ENTITY">{property.ownerEntity}</FieldRow>
                    <FieldRow label="OWNER ADDRESS">
                        {!!property.ownerAddress &&
                            <div className={[commonStyles.value, commonStyles.link].join(" ")} onClick={() => !!property.ownerAddress && searchStore.setSearch(property.ownerAddress, SearchType.Leads)}>
                                {property.ownerAddress}
                            </div>
                        }
                    </FieldRow>
                    <FieldRow label="SALE DATE">
                        {!!property.saleDate && formatDate(new Date(property.saleDate))}
                    </FieldRow>
                    <FieldRow label="SALE PRICE">
                        {!!property.salePrice && formatPrice(property.salePrice)}
                    </FieldRow>
                    <FieldRow label="PRICE/SF LAND">
                        {!!property.priceSFLand && `${formatPrice(property.priceSFLand)}/Sq-Ft`}
                    </FieldRow>
                    <FieldRow label="PRICE/ACRE LAND">
                        {!!property.priceAcreLand && `${formatPrice(property.priceAcreLand)}/Acre`}
                    </FieldRow>
                    <PropertyContactInfoPanel property={property} />
                </div>
                <div className={detailsStyles.columns}>
                    <div className={[detailsStyles.column, commonStyles.rowSeparated].join(" ")}>
                        <div className={commonStyles.header}>Demographics</div>
                        <FieldRow label="MEDIAN HOUSEHOLD INCOME">
                            {!!property.medianHouseholdIncome && formatPrice(property.medianHouseholdIncome)}
                        </FieldRow>
                        <FieldRow label="5 YEAR HOUSEHOLD INCOME GROWTH">
                            {!!property.householdIncomeGrowthNext5Years && `${formatNumber(property.householdIncomeGrowthNext5Years)}%`}
                        </FieldRow>
                        <FieldRow label="HOUSING AFFORDABILITY INDEX">
                            {!!property.housingAffordabilityIndex && formatNumber(property.housingAffordabilityIndex)}
                        </FieldRow>
                        <FieldRow label="POPULATION DENSITY">
                            {!!property.populationDensity && `${formatNumber(property.populationDensity)}/Sq-Mi`}
                        </FieldRow>
                        <FieldRow label="PAST 5 YEARS POPULATION GROWTH">
                            {!!property.populationGrowthPast5Years && `${formatNumber(property.populationGrowthPast5Years)}%`}
                        </FieldRow>
                        <FieldRow label="NEXT 5 YEARS POPULATION GROWTH">
                            {!!property.populationGrowthNext5Years && `${formatNumber(property.populationGrowthNext5Years)}%`}
                        </FieldRow>
                        <FieldRow label="PAST 5 YEARS HOUSING GROWTH">
                            {!!property.housingGrowthPast5Years && `${formatNumber(property.housingGrowthPast5Years)}%`}
                        </FieldRow>
                        <FieldRow label="NEXT 5 YEARS HOUSING GROWTH">
                            {!!property.housingGrowthNext5Years && `${formatNumber(property.housingGrowthNext5Years)}%`}
                        </FieldRow>
                    </div>
                </div>
                <div className={[detailsStyles.column, commonStyles.rowSeparated].join(" ")}>
                    <div className={commonStyles.header}>Assessment</div>
                    <FieldRow label="LAND ASSESSMENT">
                        {!!property.landAssessment && formatPrice(property.landAssessment)}
                    </FieldRow>
                    <FieldRow label="BUILDING ASSESSMENT">
                        {!!property.buildingAssessment && formatPrice(property.buildingAssessment)}
                    </FieldRow>
                    <FieldRow label="TOTAL ASSESSMENT">
                        {!!property.totalAssessment && formatPrice(property.totalAssessment)}
                    </FieldRow>
                    <FieldRow label="ANNUAL TAX BILL">
                        {!!property.annualTaxBill && formatPrice(property.annualTaxBill)}
                    </FieldRow>
                    <FieldRow label="TAX YEAR">{property.taxYear}</FieldRow>
                </div>
            </div>
        </div>
    );
}

export default observer(PropertySummary);

interface IPropertySummaryProps {
    property: IProperty;
}